import api from '../httpClient';
import { scheduleNutritionTargetUpdateToast } from '@/utils/toast-utils';
import type {
  JsonQuestionnaireSection,
  JsonQuestion,
  JsonQuestionType,
  QuestionOption,
  QuestionValidation,
  SelectOptionVisibility,
  UnitInputHint,
} from '@/components/questionnaires/json-driven/types';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function normalizeStringArray(value: unknown): string[] | undefined {
  if (Array.isArray(value)) {
    const items = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    return items.length > 0 ? items : undefined;
  }
  if (typeof value === 'string' && value.trim()) {
    const items = value.split(',').map((item) => item.trim()).filter(Boolean);
    return items.length > 0 ? items : undefined;
  }
  return undefined;
}

function normalizeOtherTextValue(value: unknown): string | string[] | undefined {
  if (Array.isArray(value)) {
    const items = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    return items.length > 0 ? items : undefined;
  }
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function resolveQuestionIdentifier(value: unknown): string | null {
  if (!isObject(value)) return null;
  return normalizeString(value._id) ?? normalizeString(value.questionId);
}

function mapCustomQuestionToJsonQuestion(
  value: unknown,
  fallbackOrder: number,
  minimumOrder: number
): JsonQuestion | null {
  if (!isObject(value)) return null;

  const questionId = resolveQuestionIdentifier(value);
  const questionText = normalizeString(value.questionText);
  const type = normalizeString(value.type) as JsonQuestionType | null;
  if (!questionId || !questionText || !type) return null;

  const options = Array.isArray(value.options)
    ? value.options
        .filter((option): option is Record<string, unknown> => isObject(option))
        .map((option) => ({
          label: String(option.label ?? ''),
          value: String(option.value ?? ''),
          ...(typeof option.triggersOtherText === 'boolean'
            ? { triggersOtherText: option.triggersOtherText }
            : {}),
        }))
        .filter((option) => option.label.trim().length > 0 && option.value.trim().length > 0)
    : undefined;

  const rawOrder = typeof value.order === 'number' && Number.isFinite(value.order)
    ? value.order
    : fallbackOrder;
  const order = rawOrder < minimumOrder ? fallbackOrder : rawOrder;

  return {
    _id: questionId,
    questionId,
    questionText,
    type,
    inputType: normalizeString(value.inputType) ?? undefined,
    options: options && options.length > 0 ? options : undefined,
    placeholder: normalizeString(value.placeholder) ?? undefined,
    validation: isObject(value.validation) ? (value.validation as JsonQuestion['validation']) : undefined,
    medicalConditions: Array.isArray(value.medicalConditions)
      ? value.medicalConditions.filter((item): item is string => typeof item === 'string')
      : undefined,
    gender: value.gender === 'male' || value.gender === 'female' || value.gender === 'all'
      ? value.gender
      : undefined,
    ageMin: typeof value.ageMin === 'number' && Number.isFinite(value.ageMin) ? value.ageMin : undefined,
    ageMax: typeof value.ageMax === 'number' && Number.isFinite(value.ageMax) ? value.ageMax : undefined,
    isConditional: typeof value.isConditional === 'boolean' ? value.isConditional : undefined,
    order,
    isActive: value.isActive === false ? false : true,
    isCustom: true,
    label: normalizeString(value.label) ?? 'Additional Questions',
  };
}

function mergeCustomQuestionsIntoSection(
  section: JsonQuestionnaireSection,
  customQuestions: unknown[]
): JsonQuestionnaireSection {
  if (!Array.isArray(customQuestions) || customQuestions.length === 0) {
    return section;
  }

  const baseQuestions = Array.isArray(section.questions) ? section.questions : [];
  const existingIds = new Set(
    baseQuestions
      .map((question) => resolveQuestionIdentifier(question))
      .filter((value): value is string => Boolean(value))
  );
  const existingMaxOrder = baseQuestions.reduce((max, question) => {
    const order = typeof question.order === 'number' && Number.isFinite(question.order)
      ? question.order
      : Number.NEGATIVE_INFINITY;
    return Math.max(max, order);
  }, Number.NEGATIVE_INFINITY);
  const firstCustomOrder =
    Number.isFinite(existingMaxOrder) ? existingMaxOrder + 1 : baseQuestions.length;

  const appendedCustomQuestions: JsonQuestion[] = [];
  customQuestions.forEach((customQuestion, index) => {
    const mapped = mapCustomQuestionToJsonQuestion(
      customQuestion,
      firstCustomOrder + index,
      firstCustomOrder
    );
    if (!mapped) return;
    if (existingIds.has(mapped._id)) return;
    existingIds.add(mapped._id);
    appendedCustomQuestions.push(mapped);
  });

  const existingCustomCount =
    typeof section.customQuestionsCount === 'number' && Number.isFinite(section.customQuestionsCount)
      ? section.customQuestionsCount
      : 0;
  const resolvedCustomCount = Math.max(existingCustomCount, customQuestions.length, appendedCustomQuestions.length);

  if (appendedCustomQuestions.length === 0) {
    if (resolvedCustomCount === 0 || (section.includesCustomQuestions === true && existingCustomCount >= resolvedCustomCount)) {
      return section;
    }
    return {
      ...section,
      includesCustomQuestions: true,
      customQuestionsCount: resolvedCustomCount,
    };
  }

  return {
    ...section,
    questions: [...baseQuestions, ...appendedCustomQuestions],
    includesCustomQuestions: true,
    customQuestionsCount: resolvedCustomCount,
  };
}

function getQuestionnaireList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }

  if (!isObject(raw)) {
    return [];
  }

  const candidates = [
    raw.questionnaires,
    raw.data,
    raw.results,
    raw.items,
    raw.core,
    raw.custom,
    raw.coreQuestionnaires,
    raw.customQuestionnaires,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
    if (isObject(candidate)) {
      const nestedList = getQuestionnaireList(candidate);
      if (nestedList.length > 0) {
        return nestedList;
      }
    }
  }

  return [];
}

function parseQuestionnaireSections(raw: unknown): JsonQuestionnaireSection[] {
  const questionnaires = getQuestionnaireList(raw);
  if (!Array.isArray(questionnaires)) return [];

  // Map API response to frontend format
  // API returns _id; list endpoint may not include questions array
  return questionnaires
    .filter((q) => {
      if (!isObject(q)) return false;
      const item = q as Record<string, unknown>;
      const hasId = typeof item._id === 'string' || typeof item.id === 'string';
      const hasTitle = typeof item.title === 'string' || typeof item.name === 'string';
      return hasId && hasTitle;
    })
    .map((q) => {
      const raw = q as Record<string, unknown>;
      const mapped: Record<string, unknown> = isObject(q) ? { ...q } : {};
      if (raw._id && !raw.id) {
        mapped.id = raw._id;
      }
      if (!mapped.title && typeof raw.name === 'string') {
        mapped.title = raw.name;
      }
      if (Array.isArray(raw.questionsByLabel)) {
        const groups = raw.questionsByLabel as QuestionnaireByIdGroupApi[];
        const flattened = groups.flatMap((g) =>
          (g.questions ?? []).map((q, index) => mapQuestionByIdToJson(q, g.label, index))
        );
        if (flattened.length > 0) {
          mapped.questions = flattened;
        }
      }
      if (!Array.isArray(mapped.questions)) {
        mapped.questions = [];
      }
      // Normalize question count from all known API keys so all tabs can render counts consistently.
      const questionCountCandidates = [
        raw.questionsCount,
        raw.templateQuestionsCount,
        raw.questionCount,
      ];
      const normalizedQuestionCount =
        questionCountCandidates.find(
          (value): value is number => typeof value === 'number' && Number.isFinite(value)
        ) ?? 0;
      mapped.questionsCount = normalizedQuestionCount;
      if (!(typeof mapped.templateQuestionsCount === 'number' && Number.isFinite(mapped.templateQuestionsCount))) {
        mapped.templateQuestionsCount = normalizedQuestionCount;
      }
      mapped.estimatedTimeMinutes =
        typeof raw.estimatedMinutes === 'number' && Number.isFinite(raw.estimatedMinutes)
          ? raw.estimatedMinutes
          : mapped.estimatedTimeMinutes ?? 0;
      mapped.assignedClientsCount =
        typeof raw.assignedClientsCount === 'number' && Number.isFinite(raw.assignedClientsCount)
          ? raw.assignedClientsCount
          : mapped.assignedClientsCount ?? 0;
      return mapped as unknown as JsonQuestionnaireSection;
    });
}

// Response types

/** Raw questionnaire list item from core/custom list API (no questions array). */
export interface QuestionnaireListItemApi {
  _id: string;
  questionnaireId?: string;
  title?: string;
  name?: string;
  fileName?: string | null;
  description?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  nutritionistId?: string | null;
  templateQuestionsCount?: number;
  /** API may send questionCount (e.g. GET /questionnaires/custom); we map to questionsCount. */
  questionCount?: number;
  estimatedMinutes?: number;
  assignedClientsCount?: number;
  assigned?: boolean;
  responsesCount?: number;
  hasDraft?: boolean;
  draftQuestionsCount?: number;
  customStatus?: string;
  status?: string;
  originalQuestionnaireId?: string;
  customQuestionsCount?: number;
  questionsCount?: number;
}

/** Raw response from GET /questionnaires/core and GET /questionnaires/custom. */
export interface QuestionnaireListApiResponse {
  questionnaires: QuestionnaireListItemApi[];
  total: number;
  nutritionistId?: string;
  activeClientsCount?: number;
  responsesThisWeek?: number;
  pendingResponsesCount?: number;
}

/** One custom-draft list item from GET /questionnaires/custom-draft (questionnaires[]). */
export interface DraftListItemApi {
  _id: string;
  questionnaireId: string;
  name?: string;
  title?: string;
  description?: string;
  status: string;
  questionCount: number;
  createdAt?: string;
  updatedAt?: string;
  originalQuestionnaireId?: string;
  assigned?: boolean;
  assignedClientsCount?: number;
  responsesCount?: number;
  estimatedMinutes?: number;
}

/** Response from GET /questionnaires/custom-draft. */
export interface GetCustomDraftApiResponse {
  total: number;
  questionnaires: DraftListItemApi[];
  activeClientsCount?: number;
  responsesThisWeek?: number;
  pendingResponsesCount?: number;
  nutritionistId?: string;
}

/** Question shape in GET /questionnaires/:id response (questionsByLabel[].questions[]). */
export interface QuestionnaireByIdQuestionApi {
  _id: string;
  questionText: string;
  type: string;
  externalId?: string | null;
  inputType?: string | null;
  options?: Array<{ label: string; value: string; triggersOtherText?: boolean }>;
  placeholder?: string | null;
  validation?: { required?: boolean; min?: number; max?: number; [key: string]: unknown };
  targetMapping?: { collection: string; field: string; [key: string]: unknown } | null;
  conditionalFields?: unknown;
  triggerMapping?: Record<string, string> | null;
  showWhen?: { questionId: string; optionValues: string[] } | null;
  showWhenAny?: Array<{ questionId: string; optionValues: string[] }> | null;
  hideWhen?: { questionId: string; values: string[] } | null;
  skipRequiredWhen?: { questionId: string; values: string[] } | null;
  unitInputHints?: unknown;
  allowOtherWithText?: boolean | null;
  optionVisibility?: SelectOptionVisibility | null;
  scaleLabels?: { min?: string; max?: string } | null;
  label?: string;
  note?: string | null;
  order?: number;
  isActive?: boolean;
  isCustom?: boolean;
  isRequired?: boolean | null;
  source?: string | null;
}

/** Group shape in GET /questionnaires/:id response (questionsByLabel[]). */
export interface QuestionnaireByIdGroupApi {
  label: string;
  questions: QuestionnaireByIdQuestionApi[];
}

/** Raw response from GET /questionnaires/:id (single questionnaire by id). */
export interface GetQuestionnaireByIdApiResponse {
  _id: string;
  questionnaireId?: string;
  title?: string;
  name?: string;
  fileName?: string | null;
  description?: string;
  isActive?: boolean;
  order?: number;
  estimatedMinutes?: number;
  questionsByLabel: QuestionnaireByIdGroupApi[];
  createdAt?: string;
  updatedAt?: string;
  nutritionistId?: string | null;
  includesCustomQuestions?: boolean;
  templateQuestionsCount?: number;
  customQuestionsCount?: number;
  hasDraft?: boolean;
  draftQuestionsCount?: number;
  customStatus?: string;
  status?: string;
  originalQuestionnaireId?: string;
}

export interface QuestionnaireSectionsSummary {
  total: number;
  nutritionistId?: string;
  includesCustomQuestions?: boolean;
  activeClientsCount?: number;
  responsesThisWeek?: number;
  pendingResponsesCount?: number;
}

export interface QuestionnaireSectionsResult extends QuestionnaireSectionsSummary {
  questionnaires: JsonQuestionnaireSection[];
}

/**
 * Response from GET /questionnaires/summary?type=core
 * Shared metrics (activeClientsCount, totalQuestionnaires, responsesThisWeek, pendingResponsesCount)
 * are the same for both core and custom: totalQuestionnaires = core + custom (excl. conditional),
 * responses/pending are across all questionnaire types.
 */
export interface QuestionnaireSummaryCoreResponse {
  type: 'core';
  /** Same for both core and custom (global). */
  activeClientsCount: number;
  /** All types excl. conditional (core + custom). Same for both core and custom. */
  totalQuestionnaires: number;
  /** All questionnaire types. Same for both core and custom. */
  responsesThisWeek: number;
  /** All questionnaire types. Same for both core and custom. */
  pendingResponsesCount: number;
  coreQuestionnairesCount: number;
  assignedCoreCount: number;
  conditionalQuestionnairesCount: number;
}

/**
 * Response from GET /questionnaires/summary?type=custom
 * Shared metrics match core response (see QuestionnaireSummaryCoreResponse).
 */
export interface QuestionnaireSummaryCustomResponse {
  type: 'custom';
  /** Same for both core and custom (global). */
  activeClientsCount: number;
  /** All types excl. conditional (core + custom). Same for both core and custom. */
  totalQuestionnaires: number;
  /** All questionnaire types. Same for both core and custom. */
  responsesThisWeek: number;
  /** All questionnaire types. Same for both core and custom. */
  pendingResponsesCount: number;
  /** Count of custom questionnaires (for "All" tab badge). */
  customQuestionnairesCount: number;
  publishedCount: number;
  assignedCustomCount: number;
  draftCount: number;
}

export type QuestionnaireSummaryResponse =
  | QuestionnaireSummaryCoreResponse
  | QuestionnaireSummaryCustomResponse;

/**
 * Dashboard metrics for questionnaires tab.
 * GET /questionnaires/summary?type=core | type=custom
 * Auth required. Use when Core or Custom tab is active to drive metric cards and filter counts.
 */
export async function getQuestionnaireSummary(
  type: 'core' | 'custom',
  nutritionistId?: string
): Promise<QuestionnaireSummaryResponse> {
  const params: Record<string, string> = { type: type === 'custom' ? 'custom' : 'core' };
  if (nutritionistId) params.nutritionistId = nutritionistId;
  const response = await api.get<QuestionnaireSummaryResponse>('/questionnaires/summary', { params });
  return response.data;
}

/**
 * Response when creating a shareable questionnaire instance.
 *
 * The backend is expected to return an identifier we can use as the public
 * `shareId`. We prefer `instanceId` but also accept `shareId` to be tolerant
 * of minor backend changes.
 */
export interface CreateQuestionnaireShareResponse {
  instanceId?: string;
  shareId?: string;
}

export interface CreateQuestionnaireCustomDraftResponse {
  message?: string;
  questionnaireId: string;
  draftQuestionsCount: number;
  draftUpdatedAt?: string;
}

export interface CreateCustomQuestionnaireDraftPayload {
  sourceQuestionnaireId: string;
  name: string;
}

export interface CreateCustomQuestionnaireDraftResponse {
  message?: string;
  questionnaireId: string;
  status?: string;
  originalQuestionnaireId?: string;
}

export interface QuestionnaireCustomQuestion {
  _id?: string;
  questionId: string;
  questionText: string;
  type: JsonQuestionType;
  inputType?: string | null;
  options?: QuestionOption[] | null;
  placeholder?: string | null;
  validation?: QuestionValidation | null;
  medicalConditions?: string[];
  gender?: 'male' | 'female' | 'all';
  ageMin?: number | null;
  ageMax?: number | null;
  isConditional?: boolean;
  order: number;
}

export interface AddCustomQuestionToQuestionnairePayload {
  questionText: string;
  type: JsonQuestionType;
  inputType?: string | null;
  options?: QuestionOption[] | null;
  placeholder?: string | null;
  validation?: QuestionValidation | null;
  medicalConditions?: string[];
  gender?: 'male' | 'female' | 'all';
  ageMin?: number | null;
  ageMax?: number | null;
  isConditional?: boolean;
}

export interface AddCustomQuestionToQuestionnaireResponse {
  message?: string;
  questionnaireId: string;
  questionId?: string;
}

export interface DeleteQuestionFromQuestionnaireResponse {
  message?: string;
  questionnaireId: string;
  questionId?: string;
}

export interface DeleteQuestionnaireResponse {
  message?: string;
  questionnaireId?: string;
}

export interface UpdateQuestionnairePayload {
  name: string;
}

export interface UpdateQuestionnaireResponse {
  message?: string;
  questionnaireId?: string;
  name?: string;
}

export interface CopyQuestionsToQuestionnairePayload {
  sourceQuestionnaireId: string;
  questionIds?: string[];
}

export interface CopyQuestionsToQuestionnaireResponse {
  message?: string;
  questionnaireId: string;
  addedCount: number;
}

export interface PublishCustomQuestionnaireResponse {
  message?: string;
  questionnaireId: string;
  status?: string;
}

export interface QuestionnaireCustomQuestionsResponse {
  questionnaireId: string;
  customQuestions: QuestionnaireCustomQuestion[];
  total: number;
  draftQuestions?: QuestionnaireCustomQuestion[];
  draftUpdatedAt?: string;
}

export interface UpdateQuestionnaireCustomDraftPayload {
  questions: QuestionnaireCustomQuestion[];
}

export interface UpdateQuestionnaireCustomDraftResponse {
  message?: string;
  questionnaireId: string;
  draftQuestionsCount: number;
  draftUpdatedAt?: string;
}

export interface PublishQuestionnaireCustomDraftResponse {
  message?: string;
  questionnaireId: string;
  publishedCount: number;
  updatedAt?: string;
}

export interface AssignQuestionnaireClient {
  clientId: string;
  deadline?: string;
  personalMessage?: string;
  /** When true, send assignment notification by email; when false, do not. */
  sendEmail?: boolean;
}

export interface AssignQuestionnairePayload {
  clients: AssignQuestionnaireClient[];
}

export interface AssignQuestionnaireResponse {
  message?: string;
}

function buildQuestionnaireSectionsResult(
  raw: unknown,
  questionnaires: JsonQuestionnaireSection[]
): QuestionnaireSectionsResult {
  const source = isObject(raw) ? raw : {};

  return {
    questionnaires,
    total:
      typeof source.total === 'number' && Number.isFinite(source.total)
        ? source.total
        : questionnaires.length,
    nutritionistId: typeof source.nutritionistId === 'string' ? source.nutritionistId : undefined,
    activeClientsCount:
      typeof source.activeClientsCount === 'number' && Number.isFinite(source.activeClientsCount)
        ? source.activeClientsCount
        : undefined,
    responsesThisWeek:
      typeof source.responsesThisWeek === 'number' && Number.isFinite(source.responsesThisWeek)
        ? source.responsesThisWeek
        : undefined,
    pendingResponsesCount:
      typeof source.pendingResponsesCount === 'number' && Number.isFinite(source.pendingResponsesCount)
        ? source.pendingResponsesCount
        : undefined,
  };
}

async function fetchQuestionnaireSectionsByPath(
  path: string,
  nutritionistId?: string,
  search?: string
): Promise<QuestionnaireSectionsResult> {
  const params: Record<string, string> = {};
  if (nutritionistId) params.nutritionistId = nutritionistId;
  if (search != null && search.trim() !== '') params.search = search.trim();
  const response = await api.get<QuestionnaireListApiResponse>(path, { params });
  const data = response.data;

  const parsed = parseQuestionnaireSections(data);

  return buildQuestionnaireSectionsResult(data, parsed);
}

export async function fetchCoreQuestionnaireSections(
  nutritionistId?: string,
  search?: string
): Promise<QuestionnaireSectionsResult> {
  return fetchQuestionnaireSectionsByPath('/questionnaires/core', nutritionistId, search);
}

export async function fetchCustomQuestionnaireSections(
  nutritionistId?: string,
  search?: string
): Promise<QuestionnaireSectionsResult> {
  return fetchQuestionnaireSectionsByPath('/questionnaires/custom', nutritionistId, search);
}

export async function fetchConditionalQuestionnaireSections(
  nutritionistId?: string,
  search?: string
): Promise<QuestionnaireSectionsResult> {
  return fetchQuestionnaireSectionsByPath('/questionnaires/conditional', nutritionistId, search);
}

export type AssignedQuestionnairesPreference = 'core' | 'custom';

export async function fetchAssignedQuestionnaireSections(
  prefer: AssignedQuestionnairesPreference,
  nutritionistId?: string,
  search?: string
): Promise<QuestionnaireSectionsResult> {
  const params: Record<string, string> = { prefer };
  if (nutritionistId) params.nutritionistId = nutritionistId;
  if (search != null && search.trim() !== '') params.search = search.trim();

  const response = await api.get<QuestionnaireListApiResponse>('/questionnaires/assigned', { params });
  const data = response.data;
  const parsed = parseQuestionnaireSections(data);

  return buildQuestionnaireSectionsResult(data, parsed);
}

function mapDraftItemToSection(draft: DraftListItemApi): JsonQuestionnaireSection {
  return {
    id: draft._id,
    questionnaireId: draft.questionnaireId,
    title: draft.title ?? draft.name ?? '',
    name: draft.name ?? draft.title,
    description: draft.description,
    questions: [],
    templateQuestionsCount: draft.questionCount,
    questionsCount: draft.questionCount,
    estimatedTimeMinutes: draft.estimatedMinutes,
    assignedClientsCount: draft.assignedClientsCount ?? 0,
    responsesCount: draft.responsesCount ?? 0,
    assigned: draft.assigned ?? false,
    hasDraft: draft.status === 'draft',
    customStatus: draft.status,
    status: draft.status,
    originalQuestionnaireId: draft.originalQuestionnaireId,
    createdAt: draft.createdAt,
    updatedAt: draft.updatedAt,
  };
}

/**
 * List custom questionnaires in "draft" status (full custom mode).
 * GET /questionnaires/custom-draft
 * Requires Authorization.
 * Response: { total, questionnaires[], activeClientsCount, ... }
 */
export async function fetchDraftCustomQuestionnaires(
  nutritionistId?: string,
  search?: string
): Promise<QuestionnaireSectionsResult> {
  const params: Record<string, string> = {};
  if (nutritionistId) params.nutritionistId = nutritionistId;
  if (search != null && search.trim() !== '') params.search = search.trim();
  const response = await api.get<GetCustomDraftApiResponse>('/questionnaires/custom-draft', {
    params,
  });
  const data = response.data;
  const questionnaires = Array.isArray(data?.questionnaires)
    ? data.questionnaires.map(mapDraftItemToSection)
    : [];
  return {
    questionnaires,
    total: typeof data?.total === 'number' && Number.isFinite(data.total) ? data.total : questionnaires.length,
    nutritionistId: typeof data?.nutritionistId === 'string' ? data.nutritionistId : undefined,
    activeClientsCount:
      typeof data?.activeClientsCount === 'number' && Number.isFinite(data.activeClientsCount)
        ? data.activeClientsCount
        : undefined,
    responsesThisWeek:
      typeof data?.responsesThisWeek === 'number' && Number.isFinite(data.responsesThisWeek)
        ? data.responsesThisWeek
        : undefined,
    pendingResponsesCount:
      typeof data?.pendingResponsesCount === 'number' && Number.isFinite(data.pendingResponsesCount)
        ? data.pendingResponsesCount
        : undefined,
  };
}

/**
 * Fetch all questionnaires from GET /questionnaires.
 */
export async function fetchAllQuestionnaireSections(
  nutritionistId?: string
): Promise<QuestionnaireSectionsResult> {
  const params = nutritionistId ? { nutritionistId } : {};
  const response = await api.get<QuestionnaireListApiResponse>('/questionnaires', { params });
  const data = response.data;
  const parsed = parseQuestionnaireSections(data);
  return buildQuestionnaireSectionsResult(data, parsed);
}

function mapQuestionByIdToJson(
  q: QuestionnaireByIdQuestionApi,
  groupLabel: string,
  groupQuestionIndex: number
): JsonQuestion {
  const labelPrefix = groupLabel
    .replace(/[^a-zA-Z]+/g, '')
    .slice(0, 3)
    .toUpperCase();
  const displayCode =
    labelPrefix.length > 0
      ? `${labelPrefix}-${String(groupQuestionIndex + 1).padStart(2, '0')}`
      : undefined;

  const rawOptions = q.options;
  const options =
    Array.isArray(rawOptions) && rawOptions.length > 0
      ? rawOptions.map((o) => ({
          label: String(o.label ?? ''),
          value: String(o.value ?? ''),
          ...(typeof o.triggersOtherText === 'boolean' ? { triggersOtherText: o.triggersOtherText } : {}),
        }))
      : undefined;

  const source =
    q.source === 'template' || q.source === 'custom' || q.source === 'copied' ? q.source : undefined;

  return {
    _id: q._id,
    questionText: q.questionText,
    type: q.type as JsonQuestion['type'],
    externalId: q.externalId ?? undefined,
    displayCode,
    inputType: q.inputType ?? undefined,
    options,
    placeholder: q.placeholder ?? undefined,
    validation: q.validation as JsonQuestion['validation'],
    isRequired: q.isRequired === true ? true : undefined,
    targetMapping: q.targetMapping ?? undefined,
    triggerMapping: q.triggerMapping ?? undefined,
    showWhen: q.showWhen ?? undefined,
    showWhenAny: q.showWhenAny ?? undefined,
    hideWhen: q.hideWhen ?? undefined,
    skipRequiredWhen: q.skipRequiredWhen ?? undefined,
    unitInputHints: (q.unitInputHints as Record<string, UnitInputHint> | undefined) ?? undefined,
    allowOtherWithText: q.allowOtherWithText ?? undefined,
    optionVisibility: q.optionVisibility ?? undefined,
    scaleLabels: q.scaleLabels ?? undefined,
    label: q.label ?? groupLabel,
    note: q.note?.trim() ? q.note.trim() : undefined,
    order: q.order,
    isActive: q.isActive,
    isCustom: q.isCustom,
    source,
  };
}

/**
 * Normalize a single questionnaire from API (unwrap nested, map _id→id, name→title).
 * Handles both legacy shape (questions[]) and GET-by-id shape (questionsByLabel[]).
 */
function normalizeSingleQuestionnaire(raw: unknown, requestedId: string): JsonQuestionnaireSection {
  if (!isObject(raw)) {
    return { id: requestedId, title: '', questions: [] };
  }
  const data = raw as Record<string, unknown>;
  const section =
    data.questionnaire && isObject(data.questionnaire)
      ? (data.questionnaire as Record<string, unknown>)
      : data;
  const mapped = { ...section } as Record<string, unknown>;

  if (Array.isArray(section.questionsByLabel)) {
    const groups = section.questionsByLabel as QuestionnaireByIdGroupApi[];
    mapped.questions = groups.flatMap((g) =>
      (g.questions ?? []).map((q, index) => mapQuestionByIdToJson(q, g.label, index))
    );
  } else if (!Array.isArray(mapped.questions)) {
    mapped.questions = section.questions ?? [];
  }

  if (section._id && !mapped.id) {
    mapped.id = section._id;
  }
  if (!mapped.id && typeof section.questionnaireId === 'string') {
    mapped.id = section.questionnaireId;
  }
  if (!mapped.id) {
    mapped.id = requestedId;
  }
  if (!mapped.title && typeof section.title === 'string') {
    mapped.title = section.title;
  }
  if (!mapped.title && typeof section.name === 'string') {
    mapped.title = section.name;
  }
  if (section.estimatedMinutes != null && mapped.estimatedTimeMinutes == null) {
    mapped.estimatedTimeMinutes = section.estimatedMinutes;
  }
  if (section.nutritionistId != null) {
    mapped.nutritionistId = section.nutritionistId;
  }
  return mapped as unknown as JsonQuestionnaireSection;
}

/**
 * Get a single questionnaire by ID with optional custom questions.
 */
export async function getQuestionnaireById(
  questionnaireId: string,
  nutritionistId?: string
): Promise<JsonQuestionnaireSection> {
  const params = nutritionistId ? { nutritionistId } : {};
  const response = await api.get<GetQuestionnaireByIdApiResponse>(`/questionnaires/${questionnaireId}`, {
    params,
  });
  return normalizeSingleQuestionnaire(response.data, questionnaireId);
}

export async function createCustomQuestionnaireDraft(
  payload: CreateCustomQuestionnaireDraftPayload
): Promise<CreateCustomQuestionnaireDraftResponse> {
  const response = await api.post<CreateCustomQuestionnaireDraftResponse>(
    '/questionnaires/custom-draft',
    payload
  );
  return response.data;
}

export async function addCustomQuestionToQuestionnaire(
  questionnaireId: string,
  payload: AddCustomQuestionToQuestionnairePayload
): Promise<AddCustomQuestionToQuestionnaireResponse> {
  const response = await api.post<AddCustomQuestionToQuestionnaireResponse>(
    `/questionnaires/${questionnaireId}/questions`,
    payload
  );
  return response.data;
}

export async function deleteQuestionFromQuestionnaire(
  questionnaireId: string,
  questionId: string
): Promise<DeleteQuestionFromQuestionnaireResponse> {
  const response = await api.delete<DeleteQuestionFromQuestionnaireResponse>(
    `/questionnaires/${questionnaireId}/questions/${questionId}`
  );
  return response.data;
}

export async function deleteQuestionnaire(
  questionnaireId: string
): Promise<DeleteQuestionnaireResponse> {
  const response = await api.delete<DeleteQuestionnaireResponse>(`/questionnaires/${questionnaireId}`);
  return response.data;
}

export async function updateQuestionnaire(
  questionnaireId: string,
  payload: UpdateQuestionnairePayload
): Promise<UpdateQuestionnaireResponse> {
  const response = await api.put<UpdateQuestionnaireResponse>(
    `/questionnaires/${questionnaireId}`,
    payload
  );
  return response.data;
}

export async function copyQuestionsToQuestionnaire(
  questionnaireId: string,
  payload: CopyQuestionsToQuestionnairePayload
): Promise<CopyQuestionsToQuestionnaireResponse> {
  const response = await api.post<CopyQuestionsToQuestionnaireResponse>(
    `/questionnaires/${questionnaireId}/questions/copy`,
    payload
  );
  return response.data;
}

export async function publishCustomQuestionnaire(
  questionnaireId: string
): Promise<PublishCustomQuestionnaireResponse> {
  const response = await api.post<PublishCustomQuestionnaireResponse>(
    `/questionnaires/${questionnaireId}/publish`,
    {}
  );
  return response.data;
}

export async function createQuestionnaireCustomDraftFromTemplate(
  questionnaireId: string
): Promise<CreateQuestionnaireCustomDraftResponse> {
  const response = await api.post<CreateQuestionnaireCustomDraftResponse>(
    `/questionnaires/${questionnaireId}/custom-questions/create-draft-from-template`,
    {}
  );

  return response.data;
}

export async function getQuestionnaireCustomQuestions(
  questionnaireId: string,
  nutritionistId?: string,
  includeDraft: boolean = false
): Promise<QuestionnaireCustomQuestionsResponse> {
  const response = await api.get<QuestionnaireCustomQuestionsResponse>(
    `/questionnaires/${questionnaireId}/custom-questions`,
    {
      params: {
        nutritionistId,
        includeDraft: includeDraft ? true : undefined,
      },
    }
  );

  return response.data;
}

export async function updateQuestionnaireCustomDraft(
  questionnaireId: string,
  payload: UpdateQuestionnaireCustomDraftPayload
): Promise<UpdateQuestionnaireCustomDraftResponse> {
  const response = await api.put<UpdateQuestionnaireCustomDraftResponse>(
    `/questionnaires/${questionnaireId}/custom-questions/draft`,
    payload
  );

  return response.data;
}

export async function publishQuestionnaireCustomDraft(
  questionnaireId: string
): Promise<PublishQuestionnaireCustomDraftResponse> {
  const response = await api.post<PublishQuestionnaireCustomDraftResponse>(
    `/questionnaires/${questionnaireId}/custom-questions/publish-draft`,
    {}
  );

  return response.data;
}

export async function assignQuestionnaire(
  questionnaireId: string,
  payload: AssignQuestionnairePayload
): Promise<AssignQuestionnaireResponse> {
  const response = await api.post<AssignQuestionnaireResponse>(
    `/questionnaires/${questionnaireId}/assign`,
    payload
  );

  return response.data;
}

/**
 * Create a shareable questionnaire instance for a specific client.
 *
 * Calls POST /questionnaires/{questionnaireId}/share with body { clientId, dates? }.
 * dates: optional array of ISO date strings ("YYYY-MM-DD") for 24hr recall or tracking;
 *        only sent when provided and non-empty.
 * Returns the resolved share identifier (instanceId/shareId) for building public URLs.
 */
export async function createQuestionnaireShare(
  questionnaireId: string,
  clientId: string,
  dates?: string[]
): Promise<string> {
  const body: { clientId: string; dates?: string[] } = { clientId };
  if (dates && dates.length > 0) {
    body.dates = dates;
  }

  const response = await api.post<CreateQuestionnaireShareResponse>(
    `/questionnaires/${questionnaireId}/share`,
    body
  );

  const data = response.data;
  const shareId = data?.instanceId ?? data?.shareId;

  if (!shareId) {
    throw new Error('Invalid response from share endpoint: missing instanceId/shareId');
  }

  return shareId;
}

/** Progress for multi-day (e.g. 24hr) dietary recall. */
export interface DietRecallProgress {
  total: number;
  completed: number;
  completedDayNumbers: number[];
}

/** Normalize API DOB to YYYY-MM-DD for date inputs and age derivation. */
function parseAssignedClientDateOfBirth(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString().slice(0, 10);
}

export interface SharedQuestionnaireMedicalCondition {
  name?: string;
  displayName?: string;
  conditionId?: string;
  conditionSubtype?: string;
}

export interface SharedQuestionnaireAssignedClient {
  age?: number;
  dateOfBirth?: string;
  gender?: string;
  sexAtBirth?: string;
  hasAllergies?: string | null;
  hasIntolerances?: string | null;
  allergies?: string[];
  foodIntolerances?: string[];
  /** Condition ids or labels from the client profile (when provided by share API). */
  medicalConditions?: string[];
  medicalConditionDetails?: SharedQuestionnaireMedicalCondition[];
  activityLevel?: string | null;
  pregnancyBreastfeedingStatus?: string | null;
  pregnancyStatus?: string | null;
  pregnancyTrimester?: string | null;
  lactationPeriod?: string | null;
  lactationGoal?: string | null;
  isTwinPregnancy?: boolean;
  breastfeedingStatus?: string | null;
  feedingType?: string | null;
}

function parseSharedQuestionnaireAssignedClient(raw: unknown): SharedQuestionnaireAssignedClient | undefined {
  if (!isObject(raw)) return undefined;

  const medicalConditionDetails = Array.isArray(raw.medicalConditionDetails)
    ? (raw.medicalConditionDetails as unknown[])
        .filter((item): item is Record<string, unknown> => isObject(item))
        .map((item) => ({
          name:
            typeof item.name === 'string'
              ? item.name
              : typeof item.conditionName === 'string'
                ? item.conditionName
                : undefined,
          displayName:
            typeof item.displayName === 'string'
              ? item.displayName
              : typeof item.conditionName === 'string'
                ? item.conditionName
                : undefined,
          conditionId:
            typeof item.conditionId === 'string'
              ? item.conditionId
              : typeof item.condition_id === 'string'
                ? item.condition_id
                : undefined,
          conditionSubtype:
            typeof item.conditionSubtype === 'string'
              ? item.conditionSubtype
              : typeof item.condition_subtype === 'string'
                ? item.condition_subtype
              : typeof item.subtype === 'string'
                ? item.subtype
                : undefined,
        }))
    : undefined;
  const medicalConditions = Array.isArray(raw.medicalConditions)
    ? raw.medicalConditions
        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .map((item) => item.trim())
    : normalizeStringArray(raw.medicalConditions);

  return {
    age: typeof raw.age === 'number' && Number.isFinite(raw.age) ? raw.age : undefined,
    dateOfBirth: parseAssignedClientDateOfBirth(raw.dateOfBirth ?? raw.date_of_birth),
    gender: typeof raw.gender === 'string' ? raw.gender : undefined,
    sexAtBirth:
      typeof raw.sexAtBirth === 'string'
        ? raw.sexAtBirth
        : typeof raw.sex_at_birth === 'string'
          ? raw.sex_at_birth
          : undefined,
    hasAllergies: typeof raw.hasAllergies === 'string' ? raw.hasAllergies : null,
    hasIntolerances: typeof raw.hasIntolerances === 'string' ? raw.hasIntolerances : null,
    allergies: normalizeStringArray(raw.allergies),
    foodIntolerances: normalizeStringArray(raw.foodIntolerances),
    medicalConditions,
    medicalConditionDetails,
    activityLevel: typeof raw.activityLevel === 'string' ? raw.activityLevel : null,
    pregnancyBreastfeedingStatus:
      typeof raw.pregnancyBreastfeedingStatus === 'string' ? raw.pregnancyBreastfeedingStatus : null,
    pregnancyStatus: typeof raw.pregnancyStatus === 'string' ? raw.pregnancyStatus : null,
    pregnancyTrimester: typeof raw.pregnancyTrimester === 'string' ? raw.pregnancyTrimester : null,
    lactationPeriod: typeof raw.lactationPeriod === 'string' ? raw.lactationPeriod : null,
    lactationGoal: typeof raw.lactationGoal === 'string' ? raw.lactationGoal : null,
    isTwinPregnancy: typeof raw.isTwinPregnancy === 'boolean' ? raw.isTwinPregnancy : undefined,
    breastfeedingStatus: typeof raw.breastfeedingStatus === 'string' ? raw.breastfeedingStatus : null,
    feedingType: typeof raw.feedingType === 'string' ? raw.feedingType : null,
  };
}

function parseQuestionnaireResponseItem(raw: unknown): QuestionnaireResponseItem | null {
  if (!isObject(raw)) return null;
  const questionId = typeof raw.questionId === 'string' ? raw.questionId.trim() : '';
  const questionText = typeof raw.questionText === 'string' ? raw.questionText : '';
  const type = typeof raw.type === 'string' ? raw.type : '';
  if (!questionId || !questionText || !type) return null;

  return {
    questionId,
    questionText,
    label: typeof raw.label === 'string' ? raw.label : undefined,
    type,
    answer: raw.answer,
    otherText: normalizeOtherTextValue(raw.otherText),
    answeredAt: typeof raw.answeredAt === 'string' ? raw.answeredAt : undefined,
    wasConditional: raw.wasConditional === true ? true : undefined,
  };
}

function parseQuestionnaireInstanceSubmissions(raw: unknown): QuestionnaireInstanceSubmissionResponse[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((item): item is Record<string, unknown> => isObject(item))
    .map((submission) => {
      const notes =
        typeof submission.notes === 'string' && submission.notes.trim().length > 0
          ? submission.notes.trim()
          : undefined;
      return {
        responses: Array.isArray(submission.responses)
          ? submission.responses
              .map(parseQuestionnaireResponseItem)
              .filter((item): item is QuestionnaireResponseItem => Boolean(item))
          : [],
        actualQuestionnaireQuestionCount:
          typeof submission.actualQuestionnaireQuestionCount === 'number'
            ? submission.actualQuestionnaireQuestionCount
            : undefined,
        initialQuestionCount:
          typeof submission.initialQuestionCount === 'number' ? submission.initialQuestionCount : undefined,
        answeredQuestionCount:
          typeof submission.answeredQuestionCount === 'number' ? submission.answeredQuestionCount : undefined,
        status: typeof submission.status === 'string' ? submission.status : undefined,
        submittedAt: typeof submission.submittedAt === 'string' ? submission.submittedAt : undefined,
        submittedBy: typeof submission.submittedBy === 'string' ? submission.submittedBy : undefined,
        ...(notes ? { notes } : {}),
      };
    });
}

export type QuestionnaireInstanceStatus = 'pending' | 'in_progress' | 'completed';

export interface QuestionnaireSavedResponse {
  questionId: string;
  answer: string | string[] | number | boolean | { value: number; unit: string } | null;
  otherText?: string | string[];
}

/** Response from GET /questionnaires/share/{shareId} when instance has recall dates. */
export interface SharedQuestionnaireResponse {
  /** Normalized section (questions, title, etc.) for rendering. */
  section: JsonQuestionnaireSection;
  /** Number of recall days (e.g. 3 for 24hr recall over 3 days). */
  numberOfDays?: number;
  /** Recall dates in YYYY-MM-DD; one form fill per date. */
  recallDates?: string[];
  /** Progress: how many days completed and which day numbers. */
  dietRecallProgress?: DietRecallProgress;
  instanceId?: string;
  sessionId?: string;
  status?: QuestionnaireInstanceStatus;
  alreadySubmitted?: boolean;
  assignedClient?: SharedQuestionnaireAssignedClient;
  /** Present when a server-side draft exists. */
  savedResponses?: QuestionnaireSavedResponse[];
  draftSavedAt?: string;
}

/**
 * Fetch a shared questionnaire instance by its public shareId.
 * Returns section for rendering plus recall metadata (numberOfDays, recallDates, dietRecallProgress)
 * when the questionnaire is a multi-day 24hr recall.
 */
export async function getSharedQuestionnaire(shareId: string): Promise<SharedQuestionnaireResponse> {
  const response = await api.get<Record<string, unknown>>(`/questionnaires/share/${shareId}`);
  const data = response.data ?? {};
  let section = normalizeSingleQuestionnaire(data, (data.questionnaireId as string) ?? '');

  const responseCustomQuestions = [
    ...(Array.isArray(data.customQuestions) ? data.customQuestions : []),
    ...((isObject(data.questionnaire) && Array.isArray(data.questionnaire.customQuestions))
      ? data.questionnaire.customQuestions
      : []),
  ];
  section = mergeCustomQuestionsIntoSection(section, responseCustomQuestions);

  const numberOfDays = typeof data.numberOfDays === 'number' ? data.numberOfDays : undefined;
  const recallDates = Array.isArray(data.recallDates)
    ? (data.recallDates as string[]).filter((d) => typeof d === 'string')
    : undefined;
  const progress = data.dietRecallProgress && typeof data.dietRecallProgress === 'object'
    ? (data.dietRecallProgress as DietRecallProgress)
    : undefined;
  const assignedClientRaw = isObject(data.assignedClient) ? data.assignedClient : undefined;
  const assignedClient = parseSharedQuestionnaireAssignedClient(assignedClientRaw);

  const savedResponses = Array.isArray(data.savedResponses)
    ? (data.savedResponses as unknown[])
        .map((item) => {
          if (!isObject(item) || typeof item.questionId !== 'string') return null;
          return {
            questionId: item.questionId,
            answer: item.answer as QuestionnaireSavedResponse['answer'],
            ...(normalizeOtherTextValue(item.otherText) ? { otherText: normalizeOtherTextValue(item.otherText) } : {}),
          } satisfies QuestionnaireSavedResponse;
        })
        .filter((item): item is QuestionnaireSavedResponse => item != null)
    : undefined;

  const rawStatus = typeof data.status === 'string' ? data.status : undefined;
  const status =
    rawStatus === 'pending' || rawStatus === 'in_progress' || rawStatus === 'completed'
      ? rawStatus
      : undefined;

  return {
    section,
    numberOfDays,
    recallDates,
    dietRecallProgress: progress,
    instanceId: typeof data.instanceId === 'string' ? data.instanceId : undefined,
    sessionId: typeof data.sessionId === 'string' ? data.sessionId : undefined,
    status,
    alreadySubmitted: data.alreadySubmitted === true,
    assignedClient,
    savedResponses: savedResponses?.length ? savedResponses : undefined,
    draftSavedAt: typeof data.draftSavedAt === 'string' ? data.draftSavedAt : undefined,
  };
}

export interface SubmitQuestionnaireResponse {
  [key: string]: unknown;
}

export interface SubmitQuestionnaireItem {
  questionId: string;
  answer: unknown;
  /** Free text for "Other" option when allowOtherWithText is true. */
  otherText?: string;
}

export interface SubmitQuestionnaireBlock {
  questionnaireId?: string;
  responses: SubmitQuestionnaireItem[];
}

export interface SubmitQuestionnairePayload {
  questionnaires: SubmitQuestionnaireBlock[];
  /** For multi-day 24hr recall: day index (1-based). */
  dayNumber?: number;
  /** For multi-day 24hr recall: date (YYYY-MM-DD) this submission is for. */
  recallDate?: string;
  /** Optional free-text notes from the client (anything else). */
  notes?: string;
}

export const INVALID_SUBMIT_QUESTIONNAIRE_PAYLOAD_MESSAGE =
  'Request body must include questionnaires array with at least one block containing responses';

/** Single response item within a questionnaire instance (GET /questionnaires/instances/:id/response). */
export interface QuestionnaireResponseItem {
  questionId: string;
  questionText: string;
  /** Section label from questionnaire definition (e.g. Demographics, Lifestyle). */
  label?: string;
  type: string;
  answer: unknown;
  /** Free text for "Other" option when allowOtherWithText was used. */
  otherText?: string | string[];
  answeredAt?: string;
  wasConditional?: boolean;
}

export interface QuestionnaireInstanceSubmissionResponse {
  responses: QuestionnaireResponseItem[];
  actualQuestionnaireQuestionCount?: number;
  initialQuestionCount?: number;
  answeredQuestionCount?: number;
  status?: string;
  submittedAt?: string;
  submittedBy?: string;
  /** Optional free-text notes submitted with this response. */
  notes?: string;
}

export interface QuestionnaireInstanceDetailResponse {
  instanceId: string;
  questionnaireId: string;
  questionnaireTitle?: string;
  questionnaireName?: string;
  clientId: string;
  nutritionistId?: string;
  status?: string;
  completedAt?: string;
  submissionsCount?: number;
  assignedClient?: SharedQuestionnaireAssignedClient;
  /** Optional free-text notes when returned at instance level. */
  notes?: string;
  submissions: QuestionnaireInstanceSubmissionResponse[];
}

/** Questionnaire instance in GET /questionnaires/responses. */
export interface QuestionnaireInstanceResponse {
  instanceId: string;
  questionnaireId?: string;
  questionnaireTitle?: string;
  questionnaireName?: string;
  clientId: string;
  nutritionistId?: string;
  status: 'pending' | 'in_progress' | 'completed';
  hasResponse: boolean;
  createdAt?: string;
  completedAt?: string;
  submittedAt?: string;
  actualQuestionnaireQuestionCount?: number;
  initialQuestionCount?: number;
  answeredQuestionCount?: number;
  responses: QuestionnaireResponseItem[] | null;
  /** Optional free-text notes when returned on the list payload. */
  notes?: string;
}

export function getQuestionnaireInstanceTotalQuestionCount(
  questionnaire: Pick<QuestionnaireInstanceResponse, 'actualQuestionnaireQuestionCount' | 'initialQuestionCount'>
): number | undefined {
  if (
    typeof questionnaire.actualQuestionnaireQuestionCount === 'number' &&
    Number.isFinite(questionnaire.actualQuestionnaireQuestionCount)
  ) {
    return questionnaire.actualQuestionnaireQuestionCount;
  }

  return typeof questionnaire.initialQuestionCount === 'number' &&
    Number.isFinite(questionnaire.initialQuestionCount)
    ? questionnaire.initialQuestionCount
    : undefined;
}

/** Response from GET /questionnaires/responses. */
export interface GetQuestionnaireResponsesResponse {
  clientId: string;
  nutritionistId?: string;
  totalInstances: number;
  completedCount: number;
  pendingCount: number;
  inProgressCount: number;
  questionnaires: QuestionnaireInstanceResponse[];
}

/** One row from GET /questionnaires/{questionnaireId}/responses (one per client sent to). */
export interface QuestionnaireResponseByQuestionnaireItem {
  instanceId: string;
  clientId: string;
  firstName: string;
  lastName: string;
  email: string;
  submittedOn: string | null;
  status: 'completed' | 'pending' | 'in_progress';
  sentAt: string;
}

/** Response from GET /questionnaires/{questionnaireId}/responses. */
export interface GetQuestionnaireResponsesByIdResponse {
  questionnaireId: string;
  total: number;
  assignedClientsCount: number;
  responsesCount: number;
  estimatedMinutes: number;
  questionsCount: number;
  responses: QuestionnaireResponseByQuestionnaireItem[];
}

/**
 * Get all responses for one questionnaire (template or custom).
 * GET /questionnaires/{questionnaireId}/responses
 * One row per client the questionnaire was sent to, with client info and status.
 */
export async function getQuestionnaireResponsesById(
  questionnaireId: string
): Promise<GetQuestionnaireResponsesByIdResponse> {
  const response = await api.get<GetQuestionnaireResponsesByIdResponse>(
    `/questionnaires/${questionnaireId}/responses`
  );
  return response.data;
}

/**
 * Get questionnaire instances/responses for a client.
 * GET /questionnaires/responses?clientId=...&nutritionistId=... (optional)
 * Nutritionists must pass clientId. Clients get their own data without params.
 */
export async function getQuestionnaireResponses(
  clientId: string,
  nutritionistId?: string
): Promise<GetQuestionnaireResponsesResponse> {
  const params: Record<string, string> = { clientId };
  if (nutritionistId) params.nutritionistId = nutritionistId;
  const response = await api.get<GetQuestionnaireResponsesResponse>('/questionnaires/responses', { params });
  return response.data;
}

/**
 * Get submitted response for a questionnaire instance.
 * GET /questionnaires/instances/{instanceId}/response
 */
export async function getQuestionnaireInstanceResponse(
  instanceId: string
): Promise<QuestionnaireInstanceDetailResponse> {
  const response = await api.get<Record<string, unknown>>(
    `/questionnaires/instances/${instanceId}/response`
  );
  const data = response.data ?? {};

  return {
    instanceId: typeof data.instanceId === 'string' ? data.instanceId : instanceId,
    questionnaireId: typeof data.questionnaireId === 'string' ? data.questionnaireId : '',
    questionnaireTitle: typeof data.questionnaireTitle === 'string' ? data.questionnaireTitle : undefined,
    questionnaireName: typeof data.questionnaireName === 'string' ? data.questionnaireName : undefined,
    clientId: typeof data.clientId === 'string' ? data.clientId : '',
    nutritionistId: typeof data.nutritionistId === 'string' ? data.nutritionistId : undefined,
    status: typeof data.status === 'string' ? data.status : undefined,
    completedAt: typeof data.completedAt === 'string' ? data.completedAt : undefined,
    submissionsCount:
      typeof data.submissionsCount === 'number' && Number.isFinite(data.submissionsCount)
        ? data.submissionsCount
        : undefined,
    assignedClient: parseSharedQuestionnaireAssignedClient(data.assignedClient),
    ...(typeof data.notes === 'string' && data.notes.trim().length > 0
      ? { notes: data.notes.trim() }
      : {}),
    submissions: parseQuestionnaireInstanceSubmissions(data.submissions),
  };
}

export interface SendQuestionnaireReminderResponse {
  message?: string;
}

/**
 * Send a reminder email for an incomplete questionnaire instance.
 * POST /questionnaires/instances/{instanceId}/remind
 */
export async function sendQuestionnaireReminder(
  instanceId: string
): Promise<SendQuestionnaireReminderResponse> {
  const response = await api.post<SendQuestionnaireReminderResponse>(
    `/questionnaires/instances/${instanceId}/remind`
  );
  return response.data;
}

/**
 * Submit questionnaire responses for a shared instance.
 * POST /questionnaires/{instanceId}/submit
 * with body { questionnaires: Array<{ responses: Array<{ questionId, answer }> }> }.
 * `questionnaireId` is optional for backward compatibility.
 */
export interface SaveQuestionnaireDraftResponse {
  success: boolean;
  message?: string;
  instanceId: string;
  status?: QuestionnaireInstanceStatus;
  answeredQuestionCount?: number;
  savedAt?: string;
  savedResponses?: QuestionnaireSavedResponse[];
}

/**
 * Save partial questionnaire progress without writing to client profile.
 * POST /questionnaires/{instanceId}/save-draft
 */
export async function saveQuestionnaireDraft(
  instanceId: string,
  payload: SubmitQuestionnairePayload
): Promise<SaveQuestionnaireDraftResponse> {
  const questionnaires = payload?.questionnaires;
  const hasValidQuestionnaires =
    Array.isArray(questionnaires) &&
    questionnaires.length > 0 &&
    questionnaires.every(
      (q) =>
        Array.isArray(q.responses) &&
        q.responses.every(
          (responseItem) =>
            typeof responseItem?.questionId === 'string' &&
            responseItem.questionId.trim().length > 0
        )
    );

  if (!hasValidQuestionnaires) {
    throw new Error(INVALID_SUBMIT_QUESTIONNAIRE_PAYLOAD_MESSAGE);
  }

  const response = await api.post<SaveQuestionnaireDraftResponse>(
    `/questionnaires/${instanceId}/save-draft`,
    payload
  );
  return response.data;
}

export async function submitQuestionnaire(
  instanceId: string,
  payload: SubmitQuestionnairePayload
): Promise<SubmitQuestionnaireResponse> {
  const questionnaires = payload?.questionnaires;
  const hasValidQuestionnaires =
    Array.isArray(questionnaires) &&
    questionnaires.length > 0 &&
    questionnaires.every(
      (q) =>
        Array.isArray(q.responses) &&
        q.responses.every(
          (responseItem) =>
            typeof responseItem?.questionId === 'string' &&
            responseItem.questionId.trim().length > 0
        )
    );

  if (!hasValidQuestionnaires) {
    throw new Error(INVALID_SUBMIT_QUESTIONNAIRE_PAYLOAD_MESSAGE);
  }

  const response = await api.post<SubmitQuestionnaireResponse>(
    `/questionnaires/${instanceId}/submit`,
    payload
  );
  scheduleNutritionTargetUpdateToast(response.data);
  return response.data;
}

/**
 * Fetch a questionnaire by ID for trigger-injected flows (e.g. public questionnaire).
 * Uses GET /questionnaires/:id without nutritionistId.
 * For unauthenticated contexts, the backend may need to expose a public route (e.g. /questionnaires/public/:id).
 */
export async function getQuestionnaireByIdForTriggers(
  questionnaireId: string,
  nutritionistId?: string
): Promise<JsonQuestionnaireSection> {
  const params: Record<string, string | boolean> = {
    excludeConsent: true,
    excludeAdditionalNotes: true,
  };
  if (nutritionistId) {
    params.nutritionistId = nutritionistId;
  }
  const response = await api.get<GetQuestionnaireByIdApiResponse>(`/questionnaires/${questionnaireId}`, {
    params,
  });
  return normalizeSingleQuestionnaire(response.data, questionnaireId);
}
